import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2, LogIn } from "lucide-react";
import axios from "axios";
import OpsoraWordmark from "@/components/ui-fleet/OpsoraWordmark";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { ROLE_LANDING } from "@/components/ProtectedRoute";

const DEMO_USERS = [
  { label: "Admin",           email: "admin@opsora.in",   password: "Admin@2025",    desc: "Sees everything" },
  { label: "Fleet Manager",   email: "fleet@jcb.in",      password: "Fleet@2025",    desc: "Scoped to Mumbai, Pune, Delhi" },
  { label: "Dealer",          email: "dealer@leo.in",     password: "Dealer@2025",   desc: "Only LEO machines + Sales/Service" },
  { label: "Service Engineer", email: "engineer@jcb.in",  password: "Engineer@2025", desc: "Fleet Monitor + Service Hub only" },
];

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      login(r.data);
      const fromPath = location.state?.from?.pathname;
      const roleHome = ROLE_LANDING[r.data.user?.role] || "/dashboard";
      navigate(fromPath || roleHome, { replace: true });
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(", ")
          : "Login failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (u) => {
    setEmail(u.email);
    setPassword(u.password);
    setError("");
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{
        background:
          "radial-gradient(1200px 600px at 80% -10%, rgba(232,136,74,0.18), transparent 60%), radial-gradient(900px 500px at -10% 110%, rgba(196,98,45,0.14), transparent 55%), var(--ivory, #FAF8F4)",
      }}
      data-testid="login-page"
    >
      <div className="w-full max-w-[420px]">
        {/* Brand */}
        <div className="flex flex-col items-start mb-8">
          <div className="flex items-center gap-2.5">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #E8884A 0%, #C4622D 100%)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 4px 14px rgba(196, 98, 45, 0.4)",
              }}
            >
              <span className="text-white font-display text-[22px] leading-none" style={{ fontWeight: 600 }}>O</span>
            </div>
            <OpsoraWordmark size="lg" />
          </div>
          <p className="mt-3 text-[13px]" style={{ color: "var(--slate-700)" }}>
            Fleet Intelligence Platform — Operator sign-in
          </p>
        </div>

        {/* Form card */}
        <form
          onSubmit={submit}
          className="glass-card p-7 rounded-2xl"
          style={{ borderRadius: 16 }}
          data-testid="login-form"
        >
          <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--slate-700)", letterSpacing: "0.08em" }}>
            Email
          </label>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            className="w-full px-3.5 py-2.5 rounded-lg mb-4 outline-none text-[14px] font-body"
            style={{
              background: "rgba(255,255,255,0.7)",
              border: "1px solid var(--glass-border-btm)",
              color: "var(--slate-900)",
            }}
            placeholder="you@company.in"
          />

          <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--slate-700)", letterSpacing: "0.08em" }}>
            Password
          </label>
          <div className="relative mb-1">
            <input
              type={show ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full px-3.5 py-2.5 rounded-lg outline-none text-[14px] font-body pr-10"
              style={{
                background: "rgba(255,255,255,0.7)",
                border: "1px solid var(--glass-border-btm)",
                color: "var(--slate-900)",
              }}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
              style={{ color: "var(--slate-500)" }}
              data-testid="login-show-password-btn"
              aria-label={show ? "Hide password" : "Show password"}
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {error && (
            <div
              className="mt-3 mb-1 px-3 py-2 rounded-lg text-[12.5px]"
              style={{
                background: "rgba(176, 58, 42, 0.08)",
                border: "1px solid rgba(176, 58, 42, 0.25)",
                color: "var(--status-red, #B03A2A)",
              }}
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full mt-5 px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-white font-semibold text-[14px] transition-transform disabled:opacity-60"
            style={{
              background: "linear-gradient(135deg, #E8884A 0%, #C4622D 100%)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 20px rgba(196,98,45,0.32)",
              cursor: loading ? "default" : "pointer",
            }}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
            {loading ? "Signing in…" : "Sign In"}
          </button>

          {/* Demo hint */}
          <button
            type="button"
            onClick={() => setShowHints(!showHints)}
            className="mt-4 text-[11.5px] underline decoration-dotted"
            style={{ color: "var(--slate-500)" }}
            data-testid="login-toggle-demo-hints"
          >
            {showHints ? "Hide" : "Show"} demo credentials
          </button>
          {showHints && (
            <div className="mt-3 space-y-1.5" data-testid="login-demo-hints">
              {DEMO_USERS.map((u) => (
                <button
                  type="button"
                  key={u.email}
                  onClick={() => fillDemo(u)}
                  data-testid={`login-demo-${u.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="w-full text-left px-3 py-2 rounded-md text-[11.5px] transition-colors"
                  style={{
                    background: "rgba(255,255,255,0.55)",
                    border: "1px solid var(--glass-border-btm)",
                    color: "var(--slate-900)",
                  }}
                >
                  <div className="flex items-baseline justify-between">
                    <span style={{ fontWeight: 600 }}>{u.label}</span>
                    <span style={{ color: "var(--slate-500)" }} className="font-mono">{u.email}</span>
                  </div>
                  <div style={{ color: "var(--slate-700)" }}>{u.desc}</div>
                </button>
              ))}
            </div>
          )}
        </form>

        <p className="mt-5 text-center text-[11px]" style={{ color: "var(--slate-500)" }}>
          Engineered by Kairoo Technologies · Your Technology Partner
        </p>
      </div>
    </div>
  );
}
